import { useQuery } from '@tanstack/react-query';
import {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import {
  isCampaignActive,
  useSponsoredListingCampaignForm,
} from 'src/components/SponsoredListings';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import {
  AdCampaignsDataContextProvider,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent, useFormattedContent } from 'src/contexts/ContentContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { EventMetricsProvider } from 'src/contexts/EventMetricsContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { hasChanged, posFieldFromValues } from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdCampaignState,
  BiddingMode,
  CampaignManagerClient,
  Event,
  FlightDuration,
  ListingMetrics,
  Permission,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../../common';
import { CancellableFormHeader } from '../../common/CancellableFormHeader';
import { modalDetails } from '../../common/Modals.css';
import { ModalProps } from '../../Modal';
import { ModalBody, ModalFooter } from '../../Modal/Modal.styled';
import { MINIMUM_BASE_BID } from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';
import { CampaignModalBodyV2 } from './CampaignModalBodyV2';
import { CampaignModalHeader } from './CampaignModalHeader';
import { CampaignModalIntroBody } from './CampaignModalIntroBody';
import { CampaignModalIntroHeader } from './CampaignModalIntroHeader';
import { ZoneTargetingMapPanel } from './ZoneTargetingMapPanel';

type CampaignModalProps = {
  campaign?: SponsoredListingsCampaign | null;
  eventId: number;
  cancelTo?: ModalProps;
};

export const CampaignModal = (props: CampaignModalProps) => {
  return (
    <AdCampaignsDataContextProvider loadSalesDetails>
      <CampaignModalForm {...props} />
    </AdCampaignsDataContextProvider>
  );
};

const CampaignModalForm = ({
  campaign,
  eventId,
  cancelTo,
}: CampaignModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { refreshData, refreshSingleCampaign } = useAdCampaignsDataContext();
  const { closeModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;

  const {
    methods,
    onSubmit,
    event,
    eventNameDisplay,
    metrics,
    getDefaultValue,
  } = useSponsoredListingCampaignForm({
    eventId: eventId,
    campaign,
    onBeforeSubmit: () => setIsLoading(true),
    onAfterSubmit: async (success) => {
      setIsLoading(false);
      if (success) {
        closeModal(true);
        // Edit existing campaign: targeted single-campaign cache update.
        // New campaign: fall back to refreshData since mergeCampaign
        // does not return the new campaignId.
        if (campaign?.campaignId && campaign.campaignId > 0) {
          await refreshSingleCampaign(campaign.campaignId);
        } else {
          await refreshData();
        }
      }
    },
    onError: () => setIsLoading(false),
  });

  const campaignId = campaign?.campaignId;
  const detailedCampaignQuery = useQuery({
    queryKey: ['campaign-detail', campaignId],
    queryFn: async () => {
      const client = new CampaignManagerClient(activeAccountWebClientConfig);
      return client.getCampaign(campaignId!);
    },
    enabled: !!campaignId && campaignId > 0,
    gcTime: 0,
  });

  // Reset form when detailed data arrives
  useEffect(() => {
    if (detailedCampaignQuery.data) {
      methods.reset(getDefaultValue(detailedCampaignQuery.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedCampaignQuery.data]);

  const detailedCampaign = detailedCampaignQuery.data ?? campaign;
  const isLoadingDetails = detailedCampaignQuery.isLoading;

  const canOpenModal = useMemo(() => {
    if (!event) return false;

    // Existing campaign requires the view permission
    if (campaign && !hasAdPlatformViewPermission) {
      return false;
    }

    // no campaign requires the create permission
    return hasAdPlatformCreatePermission;
  }, [
    campaign,
    event,
    hasAdPlatformCreatePermission,
    hasAdPlatformViewPermission,
  ]);

  if (!canOpenModal) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <CampaignModalContent
        isLoading={isLoading || isLoadingDetails}
        setIsLoading={setIsLoading}
        eventId={eventId}
        event={event!.event}
        campaign={detailedCampaign}
        cancelTo={cancelTo}
        eventNameDisplay={eventNameDisplay}
        metrics={metrics}
        onSubmit={onSubmit}
        {...methods}
      />
    </FormProvider>
  );
};

const CampaignModalContent = ({
  event,
  campaign,
  metrics,
  eventNameDisplay,
  cancelTo,
  formState,
  handleSubmit,
  watch,
  getValues,
  clearErrors,
  setError,
  setValue,
  setFocus,
  isLoading,
  setIsLoading,
  onSubmit,
}: Omit<
  ComponentProps<typeof FormProvider<SponsoredListingsCampaignInput, unknown>>,
  'children'
> &
  CampaignModalProps & {
    event: Event;
    eventNameDisplay: string;
    metrics?: ListingMetrics;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    onSubmit: (formValue: SponsoredListingsCampaignInput) => void;
  }) => {
  const flightDuration = watch('flightDuration');
  const { refreshData, refreshSingleCampaign } = useAdCampaignsDataContext();
  const { closeModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();

  // The map starts hidden. It is shown when the user switches to Specific Zones
  // (via handleTargetChange) or clicks the Edit Selection button.
  const [isMapVisible, setIsMapVisible] = useState(false);

  const requiredText = useContent(ContentId.Required);
  const currentFlightBudgetLessThanSpendText = useContent(
    ContentId.CurrentFlightBudgetLessThanSpendError
  );
  const flightDurationText = useContent(
    FLIGHT_DURATION_TO_CID[flightDuration.value ?? FlightDuration.Daily]
  );
  const baseBidExceedsBudgetText = useFormattedContent(
    FormatContentId.BaseBidExceedsBudgetError,
    [`${flightDurationText}`]
  );
  const zoneTargetingOneOrMoreZonesRequiredText = useContent(
    ContentId.ZoneTargetingOneOrMoreZonesRequired
  );

  const validateForm = useCallback(
    (
      formValues: SponsoredListingsCampaignInput
    ): {
      isValid: boolean;
      firstErrorField: keyof SponsoredListingsCampaignInput | null;
    } => {
      let isValid = true;
      let firstErrorField: keyof SponsoredListingsCampaignInput | null = null;

      // Clear all previous errors
      clearErrors();

      // Validate campaign name
      if (
        !formValues.campaignName.value ||
        formValues.campaignName.value.trim().length === 0
      ) {
        setError('campaignName', {
          type: 'required',
          message: requiredText,
        });
        if (!firstErrorField) firstErrorField = 'campaignName';
        isValid = false;
      }

      // Validate budget
      if (
        !formValues.recurringFlightBudget.value ||
        formValues.recurringFlightBudget.value <= 0
      ) {
        setError('recurringFlightBudget', {
          type: 'required',
          message: requiredText,
        });
        if (!firstErrorField) firstErrorField = 'recurringFlightBudget';
        isValid = false;
      }

      // Validate if manual bidding is selected
      if (formValues.biddingMode.value === BiddingMode.Manual) {
        if (formValues.baseBid.value == null) {
          setError('baseBid', {
            type: 'required',
            message: requiredText,
          });
          if (!firstErrorField) firstErrorField = 'baseBid';
          isValid = false;
        } else if (formValues.baseBid.value < MINIMUM_BASE_BID) {
          // Auto-correct to minimum instead of showing error
          setValue(
            'baseBid',
            posFieldFromValues(MINIMUM_BASE_BID, campaign?.baseBid)
          );
          formValues.baseBid.value = MINIMUM_BASE_BID;
        }
      }

      // Validate that the base bid cannot be more than the recurring flight budget
      if (
        formValues.biddingMode.value === BiddingMode.Manual &&
        formValues.baseBid.value &&
        // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
        (formValues.baseBid.value >
          (formValues.recurringFlightBudget.value ?? 0) ||
          (formValues.currentFlightBudget.value &&
            formValues.baseBid.value > formValues.currentFlightBudget.value))
      ) {
        setError('baseBid', {
          type: 'max',
          message: baseBidExceedsBudgetText as string,
        });
        if (!firstErrorField) firstErrorField = 'baseBid';
        isValid = false;
      }

      // Validate the current flight budget has to be greater than spend
      if (
        campaign?.activeFlight &&
        (formValues.currentFlightBudget.value || 0) <
          (campaign.activeFlight.flightSpend || 0)
      ) {
        setError('currentFlightBudget', {
          type: 'min',
          message: currentFlightBudgetLessThanSpendText,
        });
        if (!firstErrorField) firstErrorField = 'currentFlightBudget';
        isValid = false;
      }

      // Validate that when Specific Zones is selected, at least one zone must be chosen
      if (
        formValues.targetTicketClassIds?.value !== null &&
        formValues.targetTicketClassIds?.value !== undefined &&
        (formValues.targetTicketClassIds.value?.length ?? 0) === 0
      ) {
        setError('targetTicketClassIds', {
          type: 'required',
          message: zoneTargetingOneOrMoreZonesRequiredText,
        });
        if (!firstErrorField) firstErrorField = 'targetTicketClassIds';
        isValid = false;
      }

      return {
        isValid,
        firstErrorField,
      };
    },
    [
      clearErrors,
      campaign?.activeFlight,
      campaign?.baseBid,
      setError,
      requiredText,
      setValue,
      baseBidExceedsBudgetText,
      currentFlightBudgetLessThanSpendText,
      zoneTargetingOneOrMoreZonesRequiredText,
    ]
  );

  const hasChanges = useCallback(
    (formValue: SponsoredListingsCampaignInput) => {
      if (formState.defaultValues) {
        if (formValue.campaignId < 0) {
          // New campaign - all fields are changed
          return true;
        }

        return (
          hasChanged(
            formValue.campaignName,
            formState.defaultValues.campaignName
          ) ||
          hasChanged(
            formValue.flightDuration,
            formState.defaultValues.flightDuration
          ) ||
          hasChanged(
            formValue.recurringFlightBudget,
            formState.defaultValues.recurringFlightBudget
          ) ||
          hasChanged(
            formValue.targetTicketClassIds,
            formState.defaultValues.targetTicketClassIds
          ) ||
          hasChanged(
            formValue.biddingMode,
            formState.defaultValues.biddingMode
          ) ||
          hasChanged(formValue.baseBid, formState.defaultValues.baseBid) ||
          hasChanged(
            formValue.currentFlightBudget,
            formState.defaultValues.currentFlightBudget
          ) ||
          hasChanged(
            formValue.firstThreeRowsOnly,
            formState.defaultValues.firstThreeRowsOnly
          ) ||
          hasChanged(
            formValue.spendStrategy,
            formState.defaultValues.spendStrategy
          ) ||
          hasChanged(formValue.targetRoas, formState.defaultValues.targetRoas)
        );
      }
      return false;
    },
    [formState.defaultValues]
  );

  const formHasChanges = hasChanges(watch());

  const { hasCampaignsQuery } = useAdCampaignsDataContext();
  const [viewedIntroPage, setViewedIntroPage] = useState(false);

  const showIntroPage = useMemo(
    () => !campaign && !hasCampaignsQuery.data && !viewedIntroPage,
    [campaign, hasCampaignsQuery.data, viewedIntroPage]
  );

  const onSubmitHandler = useCallback(() => {
    if (showIntroPage) {
      setViewedIntroPage(true);
      return;
    }

    const formValues = getValues();
    const { isValid, firstErrorField } = validateForm(formValues);

    if (!isValid && firstErrorField && setFocus) {
      setFocus(firstErrorField);
      return;
    }

    if (isValid) {
      handleSubmit(onSubmit)();
    }
  }, [
    showIntroPage,
    getValues,
    validateForm,
    handleSubmit,
    onSubmit,
    setFocus,
  ]);

  const BodyComponent = CampaignModalBodyV2;

  const targetTicketClassIds = watch('targetTicketClassIds');
  const isSpecificZonesSelected =
    targetTicketClassIds?.value !== null &&
    targetTicketClassIds?.value !== undefined;

  // Show the three-panel layout only when: not on intro page, specific zones is
  // selected, AND the map is currently visible (user has clicked Modify or just
  // switched to Specific Zones for the first time).
  const isZoneTargetingActive =
    !showIntroPage && isSpecificZonesSelected && isMapVisible;

  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMatchMedia('mobile');

  // Use useLayoutEffect to toggle the modal-dialog max-width BEFORE paint,
  // avoiding the visible jerk caused by updateModal in useEffect.
  // On mobile, skip the maxWidth change — the zone map overlay uses fixed
  // positioning within the modal, so no dialog resize is needed.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dialog = el.closest('.modal-dialog') as HTMLElement;
    if (!dialog) return;
    if (isMobile) return;
    if (isZoneTargetingActive) {
      dialog.style.maxWidth = '100%';
    } else {
      dialog.style.maxWidth = '';
    }
  }, [isZoneTargetingActive, isMobile]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dialog = el.closest('.modal-dialog') as HTMLElement;
    if (!dialog) return;
    if (isMobile) {
      const modalWrapper = dialog.closest('.modal') as HTMLElement;
      if (modalWrapper) {
        modalWrapper.style.overflow = 'hidden';
        modalWrapper.style.padding = '0';
      }
      dialog.style.minWidth = '100vw';
      dialog.style.maxWidth = '100vw';
      dialog.style.width = '100vw';
      dialog.style.height = '100%';
      dialog.style.maxHeight = '100%';
      dialog.style.margin = '0';
      dialog.style.borderRadius = '0';
      dialog.style.display = 'flex';
      dialog.style.alignItems = 'stretch';
      const content = dialog.querySelector('.modal-content') as HTMLElement;
      if (content) {
        content.style.height = '100%';
        content.style.maxHeight = '100%';
        content.style.overflow = 'hidden';
        content.style.borderRadius = '0';
        content.style.width = '100%';
        content.style.maxWidth = '100%';
        content.style.boxSizing = 'border-box';
      }
    } else {
      const modalWrapper = dialog.closest('.modal') as HTMLElement;
      if (modalWrapper) {
        modalWrapper.style.overflow = '';
        modalWrapper.style.padding = '';
      }
      dialog.style.minWidth = '';
      dialog.style.maxWidth = '';
      dialog.style.width = '';
      dialog.style.height = '';
      dialog.style.maxHeight = '';
      dialog.style.margin = '';
      dialog.style.borderRadius = '';
      dialog.style.display = '';
      dialog.style.alignItems = '';
      const content = dialog.querySelector('.modal-content') as HTMLElement;
      if (content) {
        content.style.height = '';
        content.style.maxHeight = '';
        content.style.overflow = '';
        content.style.borderRadius = '';
        content.style.width = '';
        content.style.maxWidth = '';
        content.style.boxSizing = '';
      }
    }
  }, [isMobile]);

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;
  const hasAdPlatformEditPermission =
    useUserHasAnyOfPermissions(
      Permission.AdPlatform_CampaignsEdit,
      Permission.AdPlatform_Edit
    ) && hasAdPlatformViewPermission;

  const { showErrorDialog, genericError } = useClientLogContext();

  const isDisabled = isLoading || formState.isSubmitting;

  const canCreateCampaign =
    !isDisabled && !campaign && hasAdPlatformCreatePermission;
  const canEditCampaign =
    !isDisabled && campaign && hasAdPlatformEditPermission;

  const isPausedCampaign = campaign && !isCampaignActive(campaign);

  const onRestartHandler = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        // Step 1: If there are form edits, submit them first via mergeCampaign
        if (formHasChanges) {
          const formValues = getValues();
          const { isValid, firstErrorField } = validateForm(formValues);

          if (!isValid && firstErrorField && setFocus) {
            setFocus(firstErrorField);
            setIsLoading(false);
            return;
          }

          if (isValid) {
            const client = new CampaignManagerClient(
              activeAccountWebClientConfig
            );
            const result = await client.mergeCampaign(formValues);
            if (!result.success) {
              showErrorDialog('Sponsored Listing', {
                message: result.errorMessage,
              });
              setIsLoading(false);
              return;
            }
          }
        }

        // Step 2: Restart the campaign
        const client = new CampaignManagerClient(activeAccountWebClientConfig);
        const response = await client.updateCampaignState(
          campaign!.campaignId,
          AdCampaignState.Active
        );

        if (response.success) {
          if (campaign?.campaignId && campaign.campaignId > 0) {
            await refreshSingleCampaign(campaign.campaignId);
          } else {
            await refreshData?.();
          }
          closeModal(true);
        } else {
          showErrorDialog('updateCampaignState', {
            message: response.errorMessage || genericError,
          });
        }
      },
      (error) => {
        showErrorDialog('Restart campaign', error);
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    formHasChanges,
    getValues,
    validateForm,
    setFocus,
    activeAccountWebClientConfig,
    campaign,
    refreshSingleCampaign,
    refreshData,
    closeModal,
    showErrorDialog,
    genericError,
    setIsLoading,
  ]);

  return (
    <EventMapContextProvider event={event} venueMapOnly>
      <EventMetricsProvider event={event}>
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            width: isZoneTargetingActive ? '100vw' : undefined,
            maxWidth: isZoneTargetingActive ? '100vw' : undefined,
            overflow: 'hidden',
          }}
        >
          {/* Left side panels — always in DOM for CSS transition to work */}
          <div
            className={[
              styles.leftPanelsContainer,
              isZoneTargetingActive
                ? styles.leftPanelsContainerVisible
                : styles.leftPanelsContainerHidden,
            ].join(' ')}
          >
            <ZoneTargetingMapPanel
              disabled={isDisabled}
              onClose={() => setIsMapVisible(false)}
            />
          </div>

          {/* Right: main modal column */}
          <Stack
            direction="column"
            style={{
              height: '100%',
              minHeight: 0,
              width: isMobile ? '100%' : '600px',
              maxWidth: isMobile ? '100%' : '600px',
              flexShrink: isMobile ? 1 : 0,
              flex: isMobile ? '1 1 0' : undefined,
              overflow: 'hidden',
            }}
          >
            <CancellableFormHeader
              className={styles.modalHeader}
              cancelTo={cancelTo}
              disabled={isDisabled}
              showDialogOnCancel={formHasChanges}
            />

            {hasCampaignsQuery.isLoading ? (
              <PosSpinner />
            ) : (
              <ModalBody
                style={{
                  overflowY: 'auto' as const,
                  minHeight: 0,
                  flex: '1 1 0',
                }}
              >
                {!showIntroPage ? (
                  <CampaignModalHeader
                    event={event}
                    eventNameDisplay={eventNameDisplay}
                    metrics={metrics}
                  />
                ) : (
                  <CampaignModalIntroHeader />
                )}
                <div className={modalDetails}>
                  {!showIntroPage ? (
                    <BodyComponent
                      campaign={campaign}
                      disabled={isDisabled}
                      eventNameDisplay={eventNameDisplay}
                      event={event}
                      metrics={metrics}
                      hideZoneList={isZoneTargetingActive || !isMapVisible}
                      isMapVisible={isMapVisible}
                      onToggleMapVisible={setIsMapVisible}
                    />
                  ) : (
                    <CampaignModalIntroBody />
                  )}
                </div>
              </ModalBody>
            )}

            <ModalFooter
              style={
                isMobile
                  ? {
                      flexShrink: 0,
                      paddingLeft: vars.spacing.lg,
                      paddingRight: vars.spacing.lg,
                    }
                  : undefined
              }
            >
              <Stack direction="column" width="full" gap="l">
                <CancellableFormFooter
                  cancelTo={cancelTo}
                  disabled={isDisabled}
                  showDialogOnCancel={formHasChanges}
                  hideCloseButton
                >
                  <Stack justifyContent="spaceBetween" width="full">
                    {campaign && isCampaignActive(campaign) && (
                      <UpdateCampaignStateButton
                        className={styles.boostButtonCommon}
                        buttonStyle={{
                          backgroundColor: vars.color.surfacePrimary,
                          color: vars.color.textPrimaryV2,
                        }}
                        campaign={campaign}
                        state={AdCampaignState.Paused}
                        disabled={!canEditCampaign}
                        onBeforePause={() => setIsLoading(true)}
                        onAfterPause={async (success) => {
                          if (success) {
                            if (
                              campaign?.campaignId &&
                              campaign.campaignId > 0
                            ) {
                              await refreshSingleCampaign(campaign.campaignId);
                            } else {
                              await refreshData?.();
                            }
                            setIsLoading(false);
                            closeModal(true);
                          }
                        }}
                      />
                    )}
                    {isPausedCampaign ? (
                      <OkButton
                        className={styles.boostButtonCommon}
                        style={{ width: '100%' }}
                        variant="regular"
                        shape="rect"
                        onClick={onRestartHandler}
                        disabled={isDisabled}
                        textContentId={ContentId.Restart}
                      />
                    ) : (
                      <OkButton
                        className={styles.boostButtonCommon}
                        style={{ width: campaign ? 'auto' : '100%' }}
                        variant="regular"
                        shape="rect"
                        onClick={onSubmitHandler}
                        disabled={
                          !formHasChanges ||
                          (!showIntroPage
                            ? !(canCreateCampaign || canEditCampaign)
                            : false)
                        }
                        textContentId={
                          campaign
                            ? ContentId.Update
                            : hasCampaignsQuery.data || viewedIntroPage
                              ? ContentId.BoostEvent
                              : ContentId.GetStarted
                        }
                      />
                    )}
                  </Stack>
                </CancellableFormFooter>
              </Stack>
            </ModalFooter>
          </Stack>
        </div>
      </EventMetricsProvider>
    </EventMapContextProvider>
  );
};
